/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { merge } from '@adobe-commerce/elsie/lib';
import { CartModel, StoreConfigModel } from '@/cart/data/models';
import { state } from '@/cart/lib/state';
import { config } from '@/cart/api';

export function transformCart(data: any): CartModel | null {
  if (!data) return null;

  const model = {
    appliedGiftCards:
      data?.applied_gift_cards?.map((giftCard: any) => {
        const giftCartState = {
          code: giftCard.code ?? '',
          appliedBalance: {
            value: giftCard.applied_balance.value ?? 0,
            currency: giftCard.applied_balance.currency ?? 'USD',
          },
          currentBalance: {
            value: giftCard.current_balance.value ?? 0,
            currency: giftCard.current_balance.currency ?? 'USD',
          },
          expirationDate: giftCard.expiration_date ?? '',
        };

        const currentBalanceValue = giftCartState?.currentBalance?.value;
        const appliedBalanceValue = giftCartState?.appliedBalance?.value;
        const currency = giftCartState?.currentBalance?.currency;

        const hasBalanceDifference =
          currentBalanceValue - appliedBalanceValue > 0;

        const value = hasBalanceDifference
          ? currentBalanceValue - appliedBalanceValue
          : 0;

        return {
          ...giftCartState,
          giftCardBalance: { value, currency },
        };
      }) ?? [],
    id: data.id,
    totalQuantity: transformQuantity(data),
    totalUniqueItems: data.itemsV2.items.length,
    totalGiftOptions: transformGiftOptions(data?.prices?.gift_options),
    giftReceiptIncluded: data?.gift_receipt_included ?? false,
    printedCardIncluded: data?.printed_card_included ?? false,
    cartGiftWrapping:
      data?.available_gift_wrappings?.map((wrapItem: any) => {
        return {
          design: wrapItem.design ?? '',
          uid: wrapItem.uid,
          selected: data?.gift_wrapping?.uid === wrapItem.uid,
          image: {
            url: wrapItem?.image?.url ?? '',
            label: wrapItem.image?.label ?? '',
          },
          price: {
            currency: wrapItem?.price?.currency ?? 'USD',
            value: wrapItem?.price?.value ?? 0,
          },
        };
      }) ?? [],
    giftMessage: {
      senderName: data?.gift_message?.from ?? '',
      recipientName: data?.gift_message?.to ?? '',
      message: data?.gift_message?.message ?? '',
    },
    errors: transformErrors(data?.itemsV2),
    items: transformItems(data?.itemsV2),
    miniCartMaxItems: transformItems(data?.itemsV2).slice(
      0,
      state.config?.miniCartMaxItemsDisplay ?? 10
    ),
    total: {
      includingTax: {
        value: data.prices.grand_total.value,
        currency: data.prices.grand_total.currency,
      },
      excludingTax: {
        value: data.prices.grand_total_excluding_tax.value,
        currency: data.prices.grand_total_excluding_tax.currency,
      },
    },
    discount: calculateTotal(
      data.prices.discounts,
      data.prices.grand_total.currency
    ),
    subtotal: {
      excludingTax: {
        value: data.prices.subtotal_excluding_tax?.value,
        currency: data.prices.subtotal_excluding_tax?.currency,
      },
      includingTax: {
        value: data.prices.subtotal_including_tax?.value,
        currency: data.prices.subtotal_including_tax?.currency,
      },
      includingDiscountOnly: {
        value: data.prices.subtotal_with_discount_excluding_tax?.value,
        currency: data.prices.subtotal_with_discount_excluding_tax?.currency,
      },
    },
    appliedTaxes: transformTotalPriceModifiers(data.prices.applied_taxes),
    totalTax: calculateTotal(
      data.prices.applied_taxes,
      data.prices.grand_total.currency
    ),
    appliedDiscounts: transformTotalPriceModifiers(data.prices.discounts),
    isVirtual: data.is_virtual,
    addresses: {
      shipping: data.shipping_addresses && transformAddresses(data),
    },
    isGuestCart: !state.authenticated,
    hasOutOfStockItems: transformOutOfStockItems(data),
    hasFullyOutOfStockItems: transformFullyOutOfStockItems(data),
    appliedCoupons: data.applied_coupons,
  };

  // extend the model with custom transformer
  return merge(
    model,
    config.getConfig().models?.CartModel?.transformer?.(data)
  );
}

function transformGiftOptions(giftOptions: any) {
  return {
    giftWrappingForItems: {
      value: giftOptions?.gift_wrapping_for_items?.value ?? 0,
      currency: giftOptions?.gift_wrapping_for_items?.currency ?? 'USD',
    },
    giftWrappingForItemsInclTax: {
      value: giftOptions?.gift_wrapping_for_items_incl_tax?.value ?? 0,
      currency:
        giftOptions?.gift_wrapping_for_items_incl_tax?.currency ?? 'USD',
    },
    giftWrappingForOrder: {
      value: giftOptions?.gift_wrapping_for_order?.value ?? 0,
      currency: giftOptions?.gift_wrapping_for_order?.currency ?? 'USD',
    },
    giftWrappingForOrderInclTax: {
      value: giftOptions?.gift_wrapping_for_order_incl_tax?.value ?? 0,
      currency:
        giftOptions?.gift_wrapping_for_order_incl_tax?.currency ?? 'USD',
    },
    printedCard: {
      value: giftOptions?.printed_card?.value ?? 0,
      currency: giftOptions?.printed_card?.currency ?? 'USD',
    },
    printedCardInclTax: {
      value: giftOptions?.printed_card_incl_tax?.value ?? 0,
      currency: giftOptions?.printed_card_incl_tax?.currency ?? 'USD',
    },
  };
}

// Need to calculate total amount since it's not provided by GraphQL
function calculateTotal(data: any[], currency: string) {
  if (!data?.length)
    return {
      value: 0,
      currency,
    };

  return data.reduce(
    (acc: any, item: any) => {
      return {
        value: acc.value + item.amount.value,
        currency: item.amount.currency,
      };
    },
    { value: 0, currency }
  );
}

function getImage(config: StoreConfigModel | null | undefined, item: any) {
  return {
    // Use parent thumbnail if configured, otherwise use own variant. use the parent thumbnail as a fallback
    src: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.url
      : item.configured_variant?.thumbnail?.url || item.product.thumbnail.url,
    alt: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.label
      : item.configured_variant?.thumbnail?.label ||
        item.product.thumbnail.label,
  };
}

function getRegularPrice(item: any) {
  if (item.__typename === 'ConfigurableCartItem') {
    return {
      value:
        item.configured_variant?.price_range?.maximum_price.regular_price.value,
      currency:
        item.configured_variant?.price_range?.maximum_price.regular_price
          .currency,
    };
  }
  if (item.__typename === 'GiftCardCartItem') {
    return {
      value: item.prices.price.value,
      currency: item.prices.price.currency,
    };
  }
  return {
    value: item.prices.original_item_price.value,
    currency: item.prices.original_item_price.currency,
  };
}

function getDiscounted(item: any) {
  if (item.__typename === 'ConfigurableCartItem')
    return (
      item.configured_variant?.price_range?.maximum_price.discount.amount_off >
      0
    );

  return item.product.price_range?.maximum_price.discount.amount_off > 0;
}

function transformGiftMessage(data: any) {
  return {
    senderName: data?.gift_message?.from ?? '',
    recipientName: data?.gift_message?.to ?? '',
    message: data?.gift_message?.message ?? '',
  };
}

function transformWrappingPrice(data: any) {
  return {
    currency: data?.currency ?? 'USD',
    value: data?.value ?? 0,
  };
}

function transformItems(data: any) {
  if (!data?.items?.length) return [];

  const config = state.config;

  return data.items.map((item: any) => ({
    giftWrappingAvailable: item?.product?.gift_wrapping_available ?? false,
    giftWrappingPrice: transformWrappingPrice(
      item?.product?.gift_wrapping_price
    ),
    giftMessage: transformGiftMessage(item),
    productGiftWrapping:
      item?.available_gift_wrapping?.map((wrapItem: any) => {
        return {
          design: wrapItem.design ?? '',
          uid: wrapItem.uid,
          selected: item.gift_wrapping?.uid === wrapItem.uid,
          image: {
            url: wrapItem?.image?.url ?? '',
            label: wrapItem.image?.label ?? '',
          },
          price: {
            currency: wrapItem?.price?.currency ?? 'USD',
            value: wrapItem?.price?.value ?? 0,
          },
        };
      }) ?? [],
    itemType: item.__typename,
    uid: item.uid,
    giftMessageAvailable: transformGiftOptionsCode(
      item.product.gift_message_available
    ),
    url: {
      urlKey: item.product.url_key,
      categories: item.product.categories.map(
        (category: any) => category.url_key
      ),
    },
    canonicalUrl: item.product.canonical_url,
    categories: item.product.categories.map((category: any) => category.name),
    quantity: item.quantity,
    sku: transformSKU(item),
    topLevelSku: item.product.sku,
    name: item.product.name,
    image: getImage(config, item),
    price: {
      value: item.prices.price.value,
      currency: item.prices.price.currency,
    },
    taxedPrice: {
      value: item.prices.price_including_tax.value,
      currency: item.prices.price_including_tax.currency,
    },
    fixedProductTaxes: item.prices.fixed_product_taxes,
    rowTotal: {
      value: item.prices.row_total.value,
      currency: item.prices.row_total.currency,
    },
    rowTotalIncludingTax: {
      value: item.prices.row_total_including_tax.value,
      currency: item.prices.row_total_including_tax.currency,
    },
    links: transformLinks(item.links),
    total: {
      value: item.prices.original_row_total?.value,
      currency: item.prices.original_row_total?.currency,
    },
    discount: {
      value: item.prices.total_item_discount.value,
      currency: item.prices.total_item_discount.currency,
      label: item.prices.discounts?.map((discount: any) => discount.label),
    },
    regularPrice: getRegularPrice(item),
    discounted: getDiscounted(item),
    bundleOptions:
      item.__typename === 'BundleCartItem'
        ? transformBundleOptions(item.bundle_options)
        : null,
    selectedOptions: transformSelectedOptions(item.configurable_options)?.options,
    selectedOptionsUIDs: transformSelectedOptions(item.configurable_options)?.uids,
    customizableOptions: transformCustomizableOptions(
      item.customizable_options
    ),
    sender: item.__typename === 'GiftCardCartItem' ? item.sender_name : null,
    senderEmail:
      item.__typename === 'GiftCardCartItem' ? item.sender_email : null,
    recipient:
      item.__typename === 'GiftCardCartItem' ? item.recipient_name : null,
    recipientEmail:
      item.__typename === 'GiftCardCartItem' ? item.recipient_email : null,
    message: item.__typename === 'GiftCardCartItem' ? item.message : null,

    discountedTotal: {
      value: item.prices.row_total.value,
      currency: item.prices.row_total.currency,
    },

    onlyXLeftInStock:
      item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant?.only_x_left_in_stock
        : item.product.only_x_left_in_stock,
    lowInventory:
      item.is_available && item.product.only_x_left_in_stock !== null,
    insufficientQuantity:
      (item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant
        : item.product
      ).stock_status === 'IN_STOCK' && !item.is_available,
    outOfStock: item.product.stock_status === 'OUT_OF_STOCK',
    stockLevel: transformStockLevel(item),
    // Add support for BundleCartItem
    discountPercentage: transformDiscountPercentage(item),
    savingsAmount: transformDiscountAmount(item),
    productAttributes: transformProductAttributes(item),
  }));
}

// This helper follows the approach used in the store configuration GraphQL schema for Gift Messages,
// where a `null` value represents the "use system value" option. It requires special processing
// based on both global and product-level configurations
function transformGiftOptionsCode(data: string) {
  switch (+data) {
    case 0:
      return false;
    case 1:
      return true;
    case 2:
      return null;
    default:
      return false;
  }
}

function transformErrors(data: any) {
  const result = data?.items?.reduce((acc: any, item: any) => {
    item.errors?.forEach((error: Error) => {
      acc.push({ uid: item.uid, text: error.message });
    });

    return acc;
  }, []);

  return result?.length ? result : null;
}

function transformTotalPriceModifiers(data: any) {
  if (!data?.length) return [];

  return data.map((item: any) => ({
    amount: {
      value: item.amount.value,
      currency: item.amount.currency,
    },
    label: item.label,
    coupon: item.coupon,
  }));
}

function transformBundleOptions(data: any) {
  const bundle = data?.map((option: any) => ({
    uid: option.uid,
    label: option.label,
    value: option.values.map((value: any) => value.label).join(', '),
  }));

  const bundleOptions: { [key: string]: any } = {};

  bundle?.forEach((option: any) => {
    bundleOptions[option.label] = option.value;
  });

  return Object.keys(bundleOptions).length > 0 ? bundleOptions : null;
}

function transformSelectedOptions(data: any) {
  const selected = data?.map((option: any) => ({
    uid: option.configurable_product_option_uid,
    label: option.option_label,
    value: option.value_label,
    valueUid: option.configurable_product_option_value_uid,
  }));

  const selectedOptions: { [key: string]: any } = {};
  const selectedOptionsUIDs: { [key: string]: any } = {};

  selected?.forEach((option: any) => {
    selectedOptions[option.label] = option.value;
    selectedOptionsUIDs[option.label] = option.valueUid;
  });

  return {
    options: Object.keys(selectedOptions).length > 0 ? selectedOptions : null,
    uids: Object.keys(selectedOptionsUIDs).length > 0 ? selectedOptionsUIDs : null,
  };
}

function transformCustomizableOptions(data: any) {
  const transformedOptions = data?.map((option: any) => ({
    uid: option.customizable_option_uid,
    label: option.label,
    type: option.type,
    values: option.values.map((value: any) => ({
      uid: value.customizable_option_value_uid,
      label: value.label,
      value: value.value,
    })),
  }));

  const result: { [key: string]: any } = {};

  transformedOptions?.forEach((option: any) => {
    switch (option.type) {
      case 'field':
      case 'area':
      case 'date_time':
        result[option.label] = option.values[0].value;
        break;
      case 'radio':
      case 'drop_down':
        result[option.label] = option.values[0].label;
        break;
      case 'multiple':
      case 'checkbox':
        result[option.label] = (option.values as Array<any>).reduce(
          (previous, current) => {
            if (previous) return `${previous}, ${current.label}`;
            return current.label;
          },
          ''
        );
        break;
      case 'file': {
        const parser = new DOMParser();
        const fileAnchorElementHtml = option.values[0].value;
        const doc = parser.parseFromString(fileAnchorElementHtml, 'text/html');
        const anchorText = doc.querySelector('a')?.textContent || '';
        result[option.label] = anchorText;
        break;
      }
    }
  });
  return result;
}

function transformQuantity(data: any) {
  if (state.config?.cartSummaryDisplayTotal === 0)
    return data.itemsV2.items.length;
  if (state.config?.cartSummaryDisplayTotal === 1) return data.total_quantity;
  // Default to items count if unexpected config value or error returned
  return data.itemsV2.items.length;
}

function transformLinks(links: any) {
  return links?.length > 0
    ? {
        count: links.length,
        result: links.map((link: any) => link.title).join(', '),
      }
    : null;
}

function transformAddresses(data: any) {
  //Shipping Address bound to cart
  if (data.shipping_addresses?.length) {
    return data.shipping_addresses?.map((address: any) => ({
      countryCode: address.country.code,
      zipCode: address.postcode,
      regionCode: address.region.code,
    }));
    //Shipping Address bound to customer
  } else if (data.addresses?.length) {
    return data.addresses
      ?.filter((address: any) => address.default_shipping)
      .map(
        (address: any) =>
          address.default_shipping && {
            countryCode: address.country_code,
            zipCode: address.postcode,
            regionCode: address.region?.region_code,
          }
      );
  }

  return null;
}

function transformOutOfStockItems(data: any) {
  return data?.itemsV2?.items?.some(
    (item: any) =>
      item?.product?.stock_status === 'OUT_OF_STOCK' ||
      (item.product.stock_status === 'IN_STOCK' && !item.is_available)
  );
}

function transformStockLevel(item: any) {
  if (!item.not_available_message) return null;

  return item.product.quantity != null ? item.product.quantity : 'noNumber';
}

function transformFullyOutOfStockItems(data: any) {
  return data?.itemsV2?.items?.some(
    (item: any) => item?.product?.stock_status === 'OUT_OF_STOCK'
  );
}

// Add support for BundleCartItem in USF-1591 after LYNX-599 is completed
function transformDiscountPercentage(data: any) {
  let percent_off;

  if (data.__typename === 'ConfigurableCartItem') {
    percent_off =
      data?.configured_variant?.price_range?.maximum_price?.discount
        ?.percent_off;
  } else if (data.__typename === 'BundleCartItem') {
    return undefined;
  } else {
    percent_off =
      data?.product?.price_range?.maximum_price?.discount?.percent_off;
  }

  if (percent_off === 0) {
    return undefined;
  }
  return Math.round(percent_off);
}

// Add support for returning configured_variant sku for __typename:"ConfigurableCartItem"
function transformSKU(item: any) {
  if (item.__typename === 'ConfigurableCartItem') {
    return item.configured_variant.sku;
  }

  // If varaintSku is available return it, otherwise return product sku
  return item.product?.variantSku || item.product.sku;
}

function transformDiscountAmount(data: any) {
  let amount_off;
  let currency;

  amount_off =
    data?.prices?.original_row_total?.value - data?.prices?.row_total?.value;
  currency = data?.prices?.row_total?.currency;

  if (amount_off === 0) {
    return undefined;
  }
  return {
    value: amount_off,
    currency,
  };
}

function transformProductAttributes(item: any) {
  return item?.product?.custom_attributesV2?.items?.map((attribute: any) => {
    const transformedCode = attribute.code
      .split('_')
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return {
      ...attribute,
      code: transformedCode,
    };
  });
}
